import {
    addDoc,
    collection,
    doc,
    getDocs,
    getFirestore,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
} from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { COLLECTIONS } from '@constants/firestorePaths';

const ITEMS = 'items';

function db() {
    return getFirestore(getFirebaseApp());
}

function stripUndefined(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const next = {};
    for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'undefined') continue;
        next[k] = v;
    }
    return next;
}

function tenantClassItemsCollectionRef(tenantId) {
    return collection(db(), COLLECTIONS.TENANT_CLASSES, tenantId, ITEMS);
}

function tenantClassesSummaryRef(tenantId) {
    return doc(db(), COLLECTIONS.TENANT_CLASSES, tenantId);
}

async function refreshTenantClassesSummary(tenantId) {
    const snap = await getDocs(query(tenantClassItemsCollectionRef(tenantId), orderBy('createdAt', 'desc')));
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const byCampus = {};
    for (const r of rows) {
        const bid = r.branchId || '__none__';
        byCampus[bid] = (byCampus[bid] || 0) + 1;
    }
    await setDoc(
        tenantClassesSummaryRef(tenantId),
        {
            tenantId,
            totalClasses: rows.length,
            byCampus,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
}

/** tenantClasses/{tenantId}/items — class documents */
export function subscribeTenantClasses({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = tenantClassItemsCollectionRef(tenantId);
    const q = query(ref, orderBy('createdAt', 'desc'));
    return onSnapshot(
        q,
        (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            onNext?.(rows);
        },
        (err) => onError?.(err)
    );
}

export async function createTenantClass({ tenantId, payload }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = tenantClassItemsCollectionRef(tenantId);

    const docPayload = stripUndefined({
        ...payload,
        tenantId,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
    });

    const created = await addDoc(ref, docPayload);
    await refreshTenantClassesSummary(tenantId);
    return { id: created.id };
}

export async function updateTenantClass({ tenantId, classId, payload }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!classId) throw new Error('Missing classId');
    const ref = doc(db(), COLLECTIONS.TENANT_CLASSES, tenantId, ITEMS, classId);
    const next = stripUndefined({
        ...payload,
        tenantId,
        updatedAt: serverTimestamp(),
    });
    await updateDoc(ref, next);
    await refreshTenantClassesSummary(tenantId);
    return { id: classId };
}
